import DataTable, { type DataTableRef } from 'datatables.net-react';
import 'datatables.net-dt/css/dataTables.dataTables.css';
import DT, { type Config } from 'datatables.net';
import type { CreateInstanceProps, Flavor } from "../../lib/ModelTypes.ts";
import { datatable_language_pack } from "./DatatableLanguagePack.tsx";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

type DataTableProps = {
    items?: Flavor[];
    onChangeFlavor?: (flavor: Flavor, e: Event) => void;
    data: CreateInstanceProps;
    radioGroupName?: string;
};

type GpuFlavorLike = Flavor & {
    instance_type?: string;
    compute_type?: string;
    available_zone_name?: string;
    available_zone_id?: number;
    pci_alias?: string;
    gpu_count?: number;
    core?: number;
    usage?: number;
    quota?: number;
    ram?: number;

    gpuModel?: string;
    gpu_model?: string;
    acceleratorModel?: string;

    gpuCount?: number;
    gpu_count_alt?: number;

    gpuMemory?: number;
    gpu_memory?: number;
    gpuMemoryGb?: number;
    gpu_memory_gb?: number;
    gpuMemoryMb?: number;
    gpu_memory_mb?: number;

    vcpu?: number;
    vcpus?: number;
};

const FlavorBmsTable = ({
                            items = [],
                            onChangeFlavor,
                            data,
                            radioGroupName = "flavorSelect",
                        }: DataTableProps) => {
    const { t } = useTranslation();

    DataTable.use(DT);

    const dataTableRef = useRef<DataTableRef>(null);

    const changeFlavor = useCallback(
        (newFlavor: Flavor, e: Event) => {
            if (newFlavor.id !== data.selectedFlavor && onChangeFlavor) {
                onChangeFlavor(newFlavor, e);
            }
        },
        [data.selectedFlavor, onChangeFlavor]
    );

    const formatRamGB = (ramMb?: number) => {
        if (ramMb == null) return "-";
        return `${ramMb / 1024}GB`;
    };

    const getGpuModel = (row: GpuFlavorLike) =>
        row.pci_alias ??
        row.gpuModel ??
        row.gpu_model ??
        row.acceleratorModel ??
        "-";

    const getGpuCount = (row: GpuFlavorLike) =>
        row.gpu_count ??
        row.gpuCount ??
        row.gpu_count_alt ??
        0;

    const getVcpu = (row: GpuFlavorLike) =>
        row.core ??
        row.vcpus ??
        row.vcpu ??
        "-";

    const columns = [
        {
            data: 'name',
            width: '6%',
            orderable: false,
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            render: (_data: string, _type: string, row: Flavor) => {
                return `<input type="radio" name="${radioGroupName}" class="flavor-radio form-check-input" value="${row.id}" ${data.selectedFlavor === row.id ? 'checked' : ''} />`;
            },
        },
        { title: t("instance.instanceName"), data: 'name', width: '12%' },
        { title: "가용 영역", data: 'available_zone_name', width: '10%' },
        {
            title: "GPU 모델",
            data: 'pci_alias',
            width: '10%',
            render: (_data: string, _type: string, row: Flavor) =>
                getGpuModel(row as GpuFlavorLike)
        },
        {
            title: "GPU 개수",
            data: 'gpu_count',
            width: '8%',
            render: (_data: string, _type: string, row: Flavor) =>
                String(getGpuCount(row as GpuFlavorLike))
        },
        {
            title: "vCPU",
            data: 'core',
            width: '8%',
            render: (_data: string, _type: string, row: Flavor) =>
                String(getVcpu(row as GpuFlavorLike))
        },
        {
            title: "시스템 메모리",
            data: 'ram',
            width: '10%',
            render: (ram: number) => formatRamGB(ram)
        },
        {
            title: "사용량",
            data: 'usage',
            width: '8%',
            render: (usage: number) => t('instance.instancesNoFmt', { n: usage })
        },
        {
            title: "잔여량",
            data: 'id',
            width: '8%',
            render: (_data: string, _type: string, row: Flavor) => {
                const typedRow = row as GpuFlavorLike;
                const quota = typedRow.quota ?? 0;
                const usage = typedRow.usage ?? 0;
                const remaining = quota - usage;
                return t('instance.instancesNoFmt', { n: remaining });
            }
        },
        {
            title: "총 할당량",
            data: 'quota',
            width: '8%',
            render: (quota: number) => t('instance.instancesNoFmt', { n: quota })
        },
    ];

    const options: Config = {
        destroy: true,
        paging: false,
        searching: false,
        order: [[1, "asc"]],
        ordering: false,
        lengthChange: false,
        info: false,
        autoWidth: false,
        language: datatable_language_pack,
        columnDefs: [
            { targets: "_all", className: "dt-center" },
        ],
    };

    useEffect(() => {
        console.log("FlavorBmsTable items:", items);
    }, [items]);

    useEffect(() => {
        const tableNodeApi = dataTableRef.current?.dt();
        if (!tableNodeApi) return;

        const count = tableNodeApi.rows().count();
        const rowsData = tableNodeApi.data().toArray() as Flavor[];

        const eventSets: {
            flavorSelectRadio: HTMLInputElement | null;
            radioClickCallback: (e: Event) => void;
            rowNode: HTMLTableRowElement | null;
            rowClickCallback: (e: Event) => void;
        }[] = [];

        for (let i = 0; i < count; i++) {
            const cell = tableNodeApi.cell(i, 0).node() as HTMLElement;
            const rowNode = tableNodeApi.row(i).node() as HTMLTableRowElement | null;
            const flavorSelectRadio = cell.querySelector(
                `input[name="${radioGroupName}"]`
            ) as HTMLInputElement | null;

            if (!flavorSelectRadio || !rowNode) continue;

            const radioClickCallback = (e: Event) => {
                e.stopPropagation();
                changeFlavor(rowsData[i], e);
            };

            const rowClickCallback = (e: Event) => {
                const target = e.target as HTMLElement | null;
                if (target?.closest('input, button, a, label')) return;
                changeFlavor(rowsData[i], e);
            };

            flavorSelectRadio.addEventListener("click", radioClickCallback);
            rowNode.addEventListener("click", rowClickCallback);
            rowNode.style.cursor = "pointer";

            eventSets.push({
                flavorSelectRadio,
                radioClickCallback,
                rowNode,
                rowClickCallback,
            });
        }

        return () => {
            eventSets.forEach(({ flavorSelectRadio, radioClickCallback, rowNode, rowClickCallback }) => {
                flavorSelectRadio?.removeEventListener("click", radioClickCallback);
                rowNode?.removeEventListener("click", rowClickCallback);
            });
        };
    }, [changeFlavor, radioGroupName, items]);

    return (
        <div style={{ fontSize: "14px" }} className="ms-3 me-4">
            <DataTable
                key={`gpu-${data.selectedFlavor ?? "none"}`}
                data={items}
                columns={columns}
                className="display"
                options={options}
                ref={dataTableRef}
            >
                <thead style={{ backgroundColor: "#E2E3E5" }} />
            </DataTable>
        </div>
    );
};

export default FlavorBmsTable;